import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { handleAuthCallback } from "../../lib/api/cloud";

const DOMAIN_BASE = "cc.somebugs.com";

/** Check whether a URL is an absolute URL pointing to a different origin. */
function isExternalRedirect(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.origin !== window.location.origin;
  } catch {
    return false;
  }
}

/** Check whether a URL points to a valid *.cc.somebugs.com subdomain different from the current origin. */
function isCrossSubdomainRedirect(url: string): boolean {
  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname;

    // Must be a subdomain of the domain base (e.g., test1.cc.somebugs.com)
    if (!hostname.endsWith(`.${DOMAIN_BASE}`)) return false;

    // Must be a different origin than the current page
    return parsed.origin !== window.location.origin;
  } catch {
    return false;
  }
}

export function AuthCallback() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");

    if (!code) {
      setError(t('auth.callback.noCode'));
      return;
    }

    const redirectUri = `${window.location.origin}/auth/callback`;

    handleAuthCallback(code, redirectUri)
      .then((result) => {
        localStorage.setItem("cloud_access_token", result.accessToken);

        const redirect = state || "/";

        // If the redirect targets a different *.cc.somebugs.com subdomain,
        // pass the access token to the instance's SSO endpoint so it can
        // create a local session (the shared cookie alone is not enough
        // because the instance validates tokens via JWKS, not a shared secret).
        if (isCrossSubdomainRedirect(redirect)) {
          const ssoUrl = new URL("/api/auth/keycloak/sso", redirect);
          ssoUrl.searchParams.set("access_token", result.accessToken);
          window.location.href = ssoUrl.toString();
          return;
        }

        // External URL (different origin) — use window.location.href
        // instead of react-router navigate() which treats absolute URLs
        // as relative paths (e.g. "https://dev.home.somebugs.com/" becomes
        // a broken path segment).
        if (isExternalRedirect(redirect)) {
          window.location.href = redirect;
          return;
        }

        navigate(redirect, { replace: true });
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : t('auth.callback.authFailed'));
      });
  }, [navigate, searchParams, t]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] px-4">
        <div className="text-center">
          <h1 className="text-xl font-bold text-[#f5f5f5] mb-2">{t('auth.callback.failed')}</h1>
          <p className="text-red-400 text-sm mb-4">{error}</p>
          <a href="/" className="text-blue-400 text-sm hover:text-blue-300">
            {t('auth.callback.returnHome')}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
      <div className="text-center">
        <div className="w-6 h-6 mx-auto mb-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        <p className="text-[#999] text-sm">{t('auth.callback.completingSignIn')}</p>
      </div>
    </div>
  );
}
